import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogContentNoOverlay,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  MapPin,
  Crosshair,
  CheckCircle2,
  X,
  Upload,
  Image as ImageIcon,
  Info,
  Target,
  Loader2,
  BookOpen,
  Camera,
  Flame,
  Wrench,
  AlertTriangle,
  Zap,
  ShieldCheck,
  HelpCircle,
  Eye,
  Navigation
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Predefined hotspot icons (using emoji for now, can be replaced with custom PNGs)
const HOTSPOT_ICONS = [
  { name: 'Default', icon: '📍', iconUrl: null },
  { name: 'Info', icon: 'ℹ️', iconUrl: null },
  { name: 'Warning', icon: '⚠️', iconUrl: null },
  { name: 'Fire Safety', icon: '🧯', iconUrl: null },
  { name: 'Electrical', icon: '⚡', iconUrl: null },
  { name: 'Tool/Equipment', icon: '🔧', iconUrl: null },
  { name: 'Safety', icon: '🛡️', iconUrl: null },
  { name: 'Eye/View', icon: '👁️', iconUrl: null },
  { name: 'Question', icon: '❓', iconUrl: null },
  { name: 'Star', icon: '⭐', iconUrl: null },
];

// Predefined hotspot colors
const HOTSPOT_COLORS = [
  { name: 'Gold', value: { r: 0.77, g: 0.64, b: 0.29 }, hex: '#C5A24A' },
  { name: 'Safety Yellow', value: { r: 1, g: 0.95, b: 0 }, hex: '#FFF200' },
  { name: 'Blue', value: { r: 0, g: 0.5, b: 1 }, hex: '#0080FF' },
  { name: 'Teal', value: { r: 0, g: 0.8, b: 0.8 }, hex: '#00CCCC' },
  { name: 'Green', value: { r: 0.2, g: 0.8, b: 0.2 }, hex: '#33CC33' },
  { name: 'Orange', value: { r: 1, g: 0.5, b: 0 }, hex: '#FF8000' },
  { name: 'Red', value: { r: 1, g: 0.2, b: 0.2 }, hex: '#FF3333' },
  { name: 'Purple', value: { r: 0.6, g: 0.2, b: 0.8 }, hex: '#9933CC' },
];

// Categories for info hotspots
const HOTSPOT_CATEGORIES = [
  'Equipment',
  'Safety',
  'Process',
  'Maintenance',
  'Quality',
  'General',
];

/**
 * InfoHotspotCreator - Create or Edit info hotspots with image upload for training
 */
export function InfoHotspotCreator({
  isOpen,
  onClose,
  pointerIntersection,
  currentSweepId,
  cameraPosition,
  tourId,
  createTagFn,
  onHotspotCreated,
  onHotspotUpdated,
  editingHotspot = null  // If provided, we're in edit mode
}) {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const [step, setStep] = useState('form'); // 'form' | 'placing' | 'success'
  const [isListening, setIsListening] = useState(false);
  const [capturedPosition, setCapturedPosition] = useState(null);
  const [capturedRotation, setCapturedRotation] = useState(null); // Camera rotation at time of placement
  const [clickFeedback, setClickFeedback] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [availableEquipment, setAvailableEquipment] = useState([]);
  
  const isEditMode = !!editingHotspot;
  
  // Form data
  const [hotspotData, setHotspotData] = useState({
    label: '',
    description: '',
    category: 'General',
    color: HOTSPOT_COLORS[0],
    icon: HOTSPOT_ICONS[0],
    stemHeight: 0.15, // Default stem height in meters
    imageFile: null,
    imagePreview: null,
    linked_equipment_id: null,
  });

  // Fetch available equipment for linking
  useEffect(() => {
    const fetchEquipment = async () => {
      if (!tourId || !token) return;
      try {
        const res = await fetch(`${API_URL}/api/maintenance/equipment/tour/${tourId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          setAvailableEquipment(await res.json());
        }
      } catch (err) {
        console.error('Failed to fetch equipment:', err);
      }
    };
    if (isOpen) {
      fetchEquipment();
    }
  }, [isOpen, tourId, token]);

  // Reset or populate when dialog opens
  useEffect(() => {
    if (isOpen) {
      setStep('form');
      setIsListening(false);
      setClickFeedback(null);
      setError(null);
      
      if (editingHotspot) {
        // Edit mode - populate with existing data
        const existingColor = HOTSPOT_COLORS.find(c => 
          c.value.r === editingHotspot.color?.r && 
          c.value.g === editingHotspot.color?.g && 
          c.value.b === editingHotspot.color?.b
        ) || HOTSPOT_COLORS[0];
        
        const existingIcon = HOTSPOT_ICONS.find(i => 
          i.name === editingHotspot.icon?.name
        ) || HOTSPOT_ICONS[0];
        
        setCapturedPosition(editingHotspot.position);
        setCapturedRotation(editingHotspot.camera_rotation || null);
        setHotspotData({
          label: editingHotspot.label || '',
          description: editingHotspot.description || '',
          category: editingHotspot.category || 'General',
          color: existingColor,
          icon: existingIcon,
          stemHeight: editingHotspot.stem_height || 0.15,
          imageFile: null,
          imagePreview: editingHotspot.image_url || null,
          linked_equipment_id: editingHotspot.linked_equipment_id || null,
        });
      } else {
        // Create mode - reset everything
        setCapturedPosition(null);
        setCapturedRotation(null);
        setHotspotData({
          label: '',
          description: '',
          category: 'General',
          color: HOTSPOT_COLORS[0],
          icon: HOTSPOT_ICONS[0],
          stemHeight: 0.15,
          imageFile: null,
          imagePreview: null,
          linked_equipment_id: null,
        });
      }
    }
  }, [isOpen, editingHotspot]);

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setHotspotData(prev => ({
          ...prev,
          imageFile: file,
          imagePreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  // Store latest position in a ref for immediate access
  const latestPositionRef = useRef(null);
  
  // Update ref whenever pointerIntersection changes
  useEffect(() => {
    if (pointerIntersection?.position) {
      latestPositionRef.current = pointerIntersection.position;
    }
  }, [pointerIntersection]);

  // Handle position capture via keyboard (Space or Enter)
  useEffect(() => {
    if (!isListening) return;
    
    const handleKeyDown = (e) => {
      // Capture position on Space or Enter
      if (e.code === 'Space' || e.code === 'Enter') {
        e.preventDefault();
        
        const pos = latestPositionRef.current;
        if (!pos) {
          console.warn('No position data available');
          setError('Could not determine position. Move your cursor over the 3D model and try again.');
          return;
        }
        
        console.log('=== KEYBOARD CAPTURE ===');
        console.log('Position:', JSON.stringify(pos));
        console.log('Camera rotation:', JSON.stringify(cameraPosition?.rotation));
        
        setCapturedPosition({
          x: pos.x,
          y: pos.y,
          z: pos.z,
        });
        
        // Capture camera rotation at this moment
        if (cameraPosition?.rotation) {
          setCapturedRotation({
            x: cameraPosition.rotation.x,
            y: cameraPosition.rotation.y,
          });
          console.log('Captured rotation:', cameraPosition.rotation);
        }
        
        // Return to form
        setStep('form');
        setIsListening(false);
        setError(null);
      }
      
      // Cancel on Escape
      if (e.code === 'Escape') {
        setStep('form');
        setIsListening(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isListening]);

  // Start position capture mode
  const handleStartCapture = () => {
    setStep('placing');
    setIsListening(true);
    setError(null);
    setClickFeedback(null);
  };

  // Save hotspot (create or update)
  const handleSave = async () => {
    if (!hotspotData.label.trim()) {
      setError('Please enter a name for the hotspot');
      return;
    }
    if (!capturedPosition) {
      setError('Please set the position in the 3D view');
      return;
    }
    if (!tourId && !isEditMode) {
      setError('No tour selected. Please select a tour first.');
      console.error('handleSave: tourId is undefined');
      return;
    }
    if (!token) {
      setError('Not authenticated. Please log in again.');
      console.error('handleSave: token is undefined');
      return;
    }

    setIsSaving(true);
    setError(null);

    console.log(`=== ${isEditMode ? 'UPDATING' : 'SAVING'} INFO HOTSPOT ===`);
    console.log('Tour ID:', tourId || editingHotspot?.tour_id);
    console.log('Position:', capturedPosition);
    console.log('Label:', hotspotData.label);
    console.log('Icon:', hotspotData.icon);

    try {
      let savedHotspot;
      
      if (isEditMode) {
        // UPDATE existing hotspot
        const updateData = {
          label: hotspotData.label,
          description: hotspotData.description || '',
          position: capturedPosition,
          category: hotspotData.category,
          stem_height: hotspotData.stemHeight,
          color: hotspotData.color.value,
          icon: { name: hotspotData.icon.name, emoji: hotspotData.icon.icon },
          linked_equipment_id: hotspotData.linked_equipment_id || null,
        };
        
        // If there's a new image file, we need to use form data
        if (hotspotData.imageFile) {
          const formData = new FormData();
          formData.append('label', hotspotData.label);
          formData.append('description', hotspotData.description || '');
          formData.append('position_x', capturedPosition.x.toString());
          formData.append('position_y', capturedPosition.y.toString());
          formData.append('position_z', capturedPosition.z.toString());
          formData.append('category', hotspotData.category);
          formData.append('stem_height', hotspotData.stemHeight.toString());
          formData.append('color_r', hotspotData.color.value.r.toString());
          formData.append('color_g', hotspotData.color.value.g.toString());
          formData.append('color_b', hotspotData.color.value.b.toString());
          formData.append('icon_name', hotspotData.icon.name);
          formData.append('icon_emoji', hotspotData.icon.icon);
          formData.append('image', hotspotData.imageFile);
          if (hotspotData.linked_equipment_id) {
            formData.append('linked_equipment_id', hotspotData.linked_equipment_id);
          }
          
          const response = await fetch(`${API_URL}/api/info-hotspots/${editingHotspot.id}/with-image`, {
            method: 'PATCH',
            headers: { 'Authorization': `Bearer ${token}` },
            body: formData,
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Failed to update hotspot');
          }
          savedHotspot = await response.json();
        } else {
          // No new image, use JSON update
          const response = await fetch(`${API_URL}/api/info-hotspots/${editingHotspot.id}`, {
            method: 'PATCH',
            headers: { 
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData),
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(errorText || 'Failed to update hotspot');
          }
          savedHotspot = await response.json();
        }
        
        console.log('Info hotspot updated successfully:', savedHotspot);
        setStep('success');
        onHotspotUpdated?.(savedHotspot);
        
      } else {
        // CREATE new hotspot
        const formData = new FormData();
        formData.append('label', hotspotData.label);
        formData.append('description', hotspotData.description || '');
        formData.append('tour_id', tourId);
        formData.append('position_x', capturedPosition.x.toString());
        formData.append('position_y', capturedPosition.y.toString());
        formData.append('position_z', capturedPosition.z.toString());
        formData.append('sweep_id', currentSweepId || '');
        formData.append('category', hotspotData.category);
        formData.append('click_radius', '1.5');
        formData.append('color_r', hotspotData.color.value.r.toString());
        formData.append('color_g', hotspotData.color.value.g.toString());
        formData.append('color_b', hotspotData.color.value.b.toString());
        formData.append('icon_name', hotspotData.icon.name);
        formData.append('icon_emoji', hotspotData.icon.icon);
        formData.append('stem_height', hotspotData.stemHeight.toString());
        
        // Save camera rotation for precise navigation (captured at time of placement)
        if (capturedRotation) {
          formData.append('camera_rotation_x', capturedRotation.x.toString());
          formData.append('camera_rotation_y', capturedRotation.y.toString());
          console.log('Saving captured rotation:', capturedRotation);
        }
        
        if (hotspotData.imageFile) {
          formData.append('image', hotspotData.imageFile);
        }
        
        if (hotspotData.linked_equipment_id) {
          formData.append('linked_equipment_id', hotspotData.linked_equipment_id);
        }

        console.log('Sending POST to:', `${API_URL}/api/info-hotspots/with-image`);
        
        const response = await fetch(`${API_URL}/api/info-hotspots/with-image`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        });

        console.log('Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('API Error:', errorText);
          throw new Error(errorText || 'Failed to create hotspot');
        }

        savedHotspot = await response.json();
        console.log('Info hotspot saved successfully:', savedHotspot);

        // Create visual marker tag in Matterport
        let matterportTagId = null;
        if (createTagFn) {
          console.log('Creating visual marker tag...');
          try {
            const iconEmoji = hotspotData.icon.icon || '📍';
            matterportTagId = await createTagFn({
              label: `${iconEmoji} ${hotspotData.label}`,
              description: '', // Keep description short in SDK, full content in our dialog
              position: capturedPosition,
              stemVector: { x: 0, y: hotspotData.stemHeight, z: 0 },
              color: hotspotData.color.value,
            });
            console.log('Visual marker tag created:', matterportTagId);

            // Update the hotspot with the matterport tag ID
            if (matterportTagId && savedHotspot.id) {
              await fetch(`${API_URL}/api/info-hotspots/${savedHotspot.id}/matterport-id?matterport_tag_id=${matterportTagId}`, {
                method: 'PATCH',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              savedHotspot.matterport_tag_id = matterportTagId;
            }
          } catch (tagErr) {
            console.warn('Could not create visual marker (hotspot still saved):', tagErr);
          }
        } else {
          console.warn('createTagFn not provided - visual marker will not appear until page reload');
        }

        setStep('success');
        onHotspotCreated?.(savedHotspot);
      }
      
      console.log(`=== HOTSPOT ${isEditMode ? 'UPDATE' : 'SAVE'} COMPLETE ===`);
    } catch (err) {
      console.error('Failed to save hotspot:', err);
      setError(err.message || 'Failed to save hotspot');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* PLACEMENT MODE UI - No overlay blocking, uses keyboard capture */}
      {step === 'placing' && isListening && (
        <>
          {/* Instruction banner at top */}
          <div className="fixed top-0 left-0 right-0 z-[70] pointer-events-none">
            <div className="bg-emerald-600 text-white py-3 px-4 text-center shadow-lg">
              <div className="flex items-center justify-center gap-3">
                <div className="animate-pulse">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                  </svg>
                </div>
                <span className="font-medium">
                  Hover over the item, then press <kbd className="px-2 py-0.5 bg-white/20 rounded mx-1">Space</kbd> or <kbd className="px-2 py-0.5 bg-white/20 rounded mx-1">Enter</kbd> to capture position
                </span>
                <button 
                  onClick={(e) => { 
                    e.stopPropagation();
                    setStep('form'); 
                    setIsListening(false); 
                  }}
                  className="ml-4 px-3 py-1 bg-white/20 hover:bg-white/30 rounded text-sm pointer-events-auto"
                >
                  Cancel (Esc)
                </button>
              </div>
            </div>
          </div>
          
          {/* Position indicator at bottom - shows live position */}
          <div className="fixed bottom-20 left-1/2 -translate-x-1/2 z-[70] pointer-events-none">
            <div className="bg-black/90 text-white px-6 py-3 rounded-lg shadow-xl">
              {pointerIntersection?.position ? (
                <div className="text-center">
                  <div className="text-xs text-gray-400 mb-1">Current Position</div>
                  <div className="font-mono text-lg">
                    X: {pointerIntersection.position.x?.toFixed(2)}, 
                    Y: {pointerIntersection.position.y?.toFixed(2)}, 
                    Z: {pointerIntersection.position.z?.toFixed(2)}
                  </div>
                  <div className="text-xs text-emerald-400 mt-2 animate-pulse">
                    Press SPACE to capture this position
                  </div>
                </div>
              ) : (
                <div className="text-center text-gray-400">
                  Move cursor over the 3D model...
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Click feedback animation */}
      {clickFeedback && (
        <div 
          className="fixed pointer-events-none z-[70]"
          style={{
            left: clickFeedback.x,
            top: clickFeedback.y,
            transform: 'translate(-50%, -50%)',
          }}
        >
          <div className="relative">
            <div 
              className="absolute rounded-full bg-emerald-500 animate-ping opacity-50" 
              style={{ width: 80, height: 80, left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }} 
            />
            <div className="relative w-10 h-10 rounded-full flex items-center justify-center shadow-xl border-2 bg-emerald-500 border-emerald-300">
              <CheckCircle2 className="h-5 w-5 text-white" />
            </div>
          </div>
        </div>
      )}

      {/* Main Dialog */}
      <Dialog open={step === 'form' || step === 'success'} onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          {step === 'form' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <BookOpen className="h-5 w-5 text-primary" />
                  {isEditMode ? 'Edit Learning Hotspot' : 'Create Learning Hotspot'}
                </DialogTitle>
                <DialogDescription>
                  {isEditMode 
                    ? 'Update the details of this info point'
                    : 'Add an info point that trainees can click to learn about this item'
                  }
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 py-4">
                {/* Name */}
                <div className="space-y-2">
                  <Label htmlFor="hotspot-label">Item Name *</Label>
                  <Input
                    id="hotspot-label"
                    placeholder="e.g., Fire Extinguisher, Safety Valve..."
                    value={hotspotData.label}
                    onChange={(e) => setHotspotData(prev => ({ ...prev, label: e.target.value }))}
                    data-testid="info-hotspot-label"
                  />
                </div>

                {/* Category */}
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={hotspotData.category} 
                    onValueChange={(value) => setHotspotData(prev => ({ ...prev, category: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {HOTSPOT_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Link to Maintenance Equipment */}
                {availableEquipment.length > 0 && (
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Link to Equipment (Optional)
                    </Label>
                    <Select 
                      value={hotspotData.linked_equipment_id || 'none'} 
                      onValueChange={(value) => setHotspotData(prev => ({ 
                        ...prev, 
                        linked_equipment_id: value === 'none' ? null : value 
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select equipment..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No equipment link</SelectItem>
                        {availableEquipment.map(equip => (
                          <SelectItem key={equip.equipment_id} value={equip.equipment_id}>
                            {equip.equipment_name} ({equip.equipment_id})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">
                      Linked hotspots will show live maintenance data
                    </p>
                  </div>
                )}
                
                {/* Description */}
                <div className="space-y-2">
                  <Label htmlFor="hotspot-desc">Description / Learning Content</Label>
                  <Textarea
                    id="hotspot-desc"
                    placeholder="Describe this item, its purpose, safety information, or any learning content..."
                    value={hotspotData.description}
                    onChange={(e) => setHotspotData(prev => ({ ...prev, description: e.target.value }))}
                    rows={4}
                    data-testid="info-hotspot-description"
                  />
                </div>

                {/* Image Upload */}
                <div className="space-y-2">
                  <Label>Photo (Optional)</Label>
                  <div 
                    className={cn(
                      "border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors",
                      hotspotData.imagePreview 
                        ? "border-primary bg-primary/5" 
                        : "border-muted-foreground/25 hover:border-primary/50"
                    )}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {hotspotData.imagePreview ? (
                      <div className="space-y-2">
                        <img 
                          src={hotspotData.imagePreview} 
                          alt="Preview" 
                          className="max-h-40 mx-auto rounded-lg object-contain"
                        />
                        <p className="text-xs text-muted-foreground">Click to change image</p>
                      </div>
                    ) : (
                      <div className="py-4">
                        <Camera className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                        <p className="text-sm text-muted-foreground">
                          Click to upload a photo of the item
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          JPG, PNG up to 5MB
                        </p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                </div>

                {/* Position Capture */}
                <div className="space-y-2">
                  <Label>3D Position *</Label>
                  {capturedPosition ? (
                    <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                      <div className="flex-1">
                        <span className="text-sm font-medium text-emerald-600">Position set!</span>
                        <span className="text-xs text-muted-foreground ml-2 font-mono">
                          ({capturedPosition.x.toFixed(1)}, {capturedPosition.y.toFixed(1)}, {capturedPosition.z.toFixed(1)})
                        </span>
                        {capturedRotation && (
                          <span className="text-xs text-blue-500 ml-2 font-mono">
                            rot: ({capturedRotation.x.toFixed(1)}, {capturedRotation.y.toFixed(1)})
                          </span>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={handleStartCapture}
                      >
                        Change
                      </Button>
                    </div>
                  ) : (
                    <Button
                      variant="outline"
                      className="w-full border-primary/50 text-primary hover:bg-primary/10"
                      onClick={handleStartCapture}
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Click to Set Position in 3D
                    </Button>
                  )}
                </div>

                {/* Icon Selection */}
                <div className="space-y-2">
                  <Label>Marker Icon</Label>
                  <div className="flex flex-wrap gap-2">
                    {HOTSPOT_ICONS.map((iconOption) => (
                      <button
                        key={iconOption.name}
                        className={cn(
                          "w-10 h-10 rounded-lg border-2 transition-all flex items-center justify-center text-xl",
                          hotspotData.icon.name === iconOption.name 
                            ? "border-primary bg-primary/10 scale-110 ring-2 ring-offset-2 ring-primary" 
                            : "border-muted hover:border-primary/50 hover:scale-105 bg-muted/30"
                        )}
                        onClick={() => setHotspotData(prev => ({ ...prev, icon: iconOption }))}
                        title={iconOption.name}
                      >
                        {iconOption.icon}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Color */}
                <div className="space-y-2">
                  <Label>Marker Color</Label>
                  <div className="flex flex-wrap gap-2">
                    {HOTSPOT_COLORS.map((color) => (
                      <button
                        key={color.name}
                        className={cn(
                          "w-8 h-8 rounded-lg border-2 transition-all",
                          hotspotData.color.name === color.name 
                            ? "border-foreground scale-110 ring-2 ring-offset-2 ring-primary" 
                            : "border-transparent hover:scale-105"
                        )}
                        style={{ backgroundColor: color.hex }}
                        onClick={() => setHotspotData(prev => ({ ...prev, color }))}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>

                {/* Stem Height */}
                <div className="space-y-2">
                  <Label>Tag Line Height</Label>
                  <div className="flex items-center gap-4">
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.05"
                      value={hotspotData.stemHeight}
                      onChange={(e) => setHotspotData(prev => ({ ...prev, stemHeight: parseFloat(e.target.value) }))}
                      className="flex-1 h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                    />
                    <span className="text-sm font-mono w-16 text-right">{hotspotData.stemHeight.toFixed(2)}m</span>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Adjust the height of the line connecting the tag to its anchor point
                  </p>
                </div>

                {error && (
                  <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 text-sm text-destructive">
                    {error}
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={onClose}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleSave} 
                  disabled={!hotspotData.label.trim() || !capturedPosition || isSaving}
                  className="gap-2"
                >
                  {isSaving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      {isEditMode ? 'Updating...' : 'Saving...'}
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4" />
                      {isEditMode ? 'Update Hotspot' : 'Create Hotspot'}
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          )}

          {step === 'success' && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-emerald-600">
                  <CheckCircle2 className="h-5 w-5" />
                  {isEditMode ? 'Hotspot Updated!' : 'Hotspot Created!'}
                </DialogTitle>
              </DialogHeader>
              
              <div className="py-6 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/20 flex items-center justify-center">
                  <BookOpen className="h-8 w-8 text-emerald-500" />
                </div>
                <p className="text-lg font-medium">&quot;{hotspotData.label}&quot;</p>
                <p className="text-sm text-muted-foreground mt-2">
                  {isEditMode 
                    ? 'Your changes have been saved.'
                    : 'Trainees can now click on this hotspot to see the learning content.'
                  }
                </p>
              </div>
              
              <DialogFooter className="flex-col sm:flex-row gap-2">
                {!isEditMode && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setStep('form');
                      setCapturedPosition(null);
                      setHotspotData({
                        label: '',
                        description: '',
                        category: 'General',
                        color: HOTSPOT_COLORS[0],
                        icon: HOTSPOT_ICONS[0],
                        imageFile: null,
                        imagePreview: null,
                      });
                    }}
                    className="w-full sm:w-auto"
                  >
                    Add Another
                  </Button>
                )}
                <Button onClick={onClose} className="w-full sm:w-auto">
                  Done
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Loading overlay */}
      {isSaving && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="bg-background p-6 flex items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
            <span>Creating hotspot...</span>
          </Card>
        </div>
      )}
    </>
  );
}

/**
 * InfoHotspotViewer - Dialog to show hotspot info when clicked
 */
export function InfoHotspotViewer({
  hotspot,
  isOpen,
  onClose,
  navigateToSweep  // Function to navigate with rotation
}) {
  const { token } = useAuth();
  const [equipmentData, setEquipmentData] = useState(null);
  const [prediction, setPrediction] = useState(null);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);

  // Handle navigation with precise camera rotation
  const handleNavigate = async () => {
    if (!hotspot?.sweep_id || !navigateToSweep) return;
    
    setIsNavigating(true);
    try {
      const options = { transition: 'fly', transitionTime: 1500 };
      if (hotspot.camera_rotation) {
        options.rotation = hotspot.camera_rotation;
      }
      await navigateToSweep(hotspot.sweep_id, options);
    } catch (err) {
      console.error('Navigation failed:', err);
    }
    setIsNavigating(false);
  };

  // Fetch equipment data if hotspot is linked to equipment
  useEffect(() => {
    const fetchEquipmentData = async () => {
      if (!hotspot?.linked_equipment_id || !token) {
        setEquipmentData(null);
        setPrediction(null);
        return;
      }

      setLoadingEquipment(true);
      try {
        // Fetch equipment KPI
        const kpiRes = await fetch(`${API_URL}/api/maintenance/equipment/${hotspot.linked_equipment_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (kpiRes.ok) {
          setEquipmentData(await kpiRes.json());
        }

        // Fetch predictions
        const predRes = await fetch(`${API_URL}/api/maintenance/predictions/${hotspot.linked_equipment_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (predRes.ok) {
          setPrediction(await predRes.json());
        }
      } catch (err) {
        console.error('Failed to fetch equipment data:', err);
      } finally {
        setLoadingEquipment(false);
      }
    };

    if (isOpen && hotspot?.linked_equipment_id) {
      fetchEquipmentData();
    }
  }, [isOpen, hotspot?.linked_equipment_id, token]);

  if (!hotspot || !isOpen) return null;

  const getRiskColor = (level) => {
    const colors = {
      low: 'bg-green-500',
      medium: 'bg-yellow-500',
      high: 'bg-orange-500',
      critical: 'bg-red-500'
    };
    return colors[level] || 'bg-gray-500';
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContentNoOverlay className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {hotspot.icon?.emoji && (
              <span className="text-2xl">{hotspot.icon.emoji}</span>
            )}
            {hotspot.category && (
              <Badge variant="secondary" className="text-xs">
                {hotspot.category}
              </Badge>
            )}
            {hotspot.linked_equipment_id && (
              <Badge className="bg-cyan-500 text-white text-xs">
                <Wrench className="h-3 w-3 mr-1" />
                Equipment
              </Badge>
            )}
          </div>
          <DialogTitle className="text-xl">{hotspot.label}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Image */}
          {hotspot.image_url && (
            <div className="rounded-lg overflow-hidden border bg-muted">
              <img 
                src={hotspot.image_url} 
                alt={hotspot.label}
                className="w-full h-auto max-h-64 object-contain"
              />
            </div>
          )}
          
          {/* Description */}
          {hotspot.description && (
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <p className="text-muted-foreground whitespace-pre-wrap">
                {hotspot.description}
              </p>
            </div>
          )}

          {/* Equipment Maintenance Data */}
          {hotspot.linked_equipment_id && (
            <div className="border-t pt-4">
              <h4 className="text-sm font-medium flex items-center gap-2 mb-3">
                <Wrench className="h-4 w-4 text-cyan-500" />
                Maintenance Status
              </h4>
              
              {loadingEquipment ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : equipmentData ? (
                <div className="space-y-3">
                  {/* KPI Cards */}
                  <div className="grid grid-cols-3 gap-2">
                    <div className="bg-muted rounded-lg p-2 text-center">
                      <div className="text-lg font-bold text-blue-500">
                        {equipmentData.current_total_cycles?.toLocaleString() || 0}
                      </div>
                      <div className="text-xs text-muted-foreground">Cycles</div>
                    </div>
                    <div className="bg-muted rounded-lg p-2 text-center">
                      <div className="text-lg font-bold text-amber-500">
                        {prediction?.percent_life_used || 0}%
                      </div>
                      <div className="text-xs text-muted-foreground">Life Used</div>
                    </div>
                    <div className="bg-muted rounded-lg p-2 text-center">
                      <Badge className={`${getRiskColor(equipmentData.risk_level)} text-white text-xs`}>
                        {equipmentData.risk_level?.toUpperCase() || 'N/A'}
                      </Badge>
                      <div className="text-xs text-muted-foreground mt-1">Risk</div>
                    </div>
                  </div>

                  {/* Alert Message */}
                  {prediction?.alert_message && (
                    <div className={cn(
                      "p-2 rounded-lg text-sm flex items-start gap-2",
                      prediction.risk_level === 'critical' ? 'bg-red-500/20 text-red-600 dark:text-red-400' :
                      prediction.risk_level === 'high' ? 'bg-orange-500/20 text-orange-600 dark:text-orange-400' :
                      'bg-blue-500/20 text-blue-600 dark:text-blue-400'
                    )}>
                      <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>{prediction.alert_message}</span>
                    </div>
                  )}

                  {/* Last Maintenance */}
                  {equipmentData.last_maintenance_date && (
                    <div className="text-xs text-muted-foreground">
                      Last maintenance: {new Date(equipmentData.last_maintenance_date).toLocaleDateString()}
                    </div>
                  )}

                  {/* Log Maintenance Button */}
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => window.open(`/maintenance/${hotspot.linked_equipment_id}`, '_blank')}
                  >
                    <Wrench className="h-4 w-4 mr-2" />
                    Log Maintenance
                  </Button>
                </div>
              ) : (
                <div className="text-center py-4 text-muted-foreground text-sm">
                  <p>Equipment data not available</p>
                  <p className="text-xs">ID: {hotspot.linked_equipment_id}</p>
                </div>
              )}
            </div>
          )}

          {/* No content message */}
          {!hotspot.image_url && !hotspot.description && !hotspot.linked_equipment_id && (
            <div className="text-center py-6 text-muted-foreground">
              <Info className="h-10 w-10 mx-auto mb-2 opacity-50" />
              <p>No additional information available</p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex gap-2">
          {hotspot.sweep_id && navigateToSweep && (
            <Button 
              variant="outline" 
              onClick={handleNavigate}
              disabled={isNavigating}
              className="flex-1"
            >
              {isNavigating ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Navigating...
                </>
              ) : (
                <>
                  <Navigation className="h-4 w-4 mr-2" />
                  Go to Location
                </>
              )}
            </Button>
          )}
          <Button onClick={onClose} className={hotspot.sweep_id && navigateToSweep ? "flex-1" : "w-full"}>
            Close
          </Button>
        </DialogFooter>
      </DialogContentNoOverlay>
    </Dialog>
  );
}

export default InfoHotspotCreator;
